// Browser-safe definitions for the editorial "best of" round-ups
// at /library/best and /library/best/<slug>.
//
// Each round-up is a hand-written editorial frame (intro, selection criteria,
// FAQ) over a slice of The Vibeary. The entries themselves come from the
// library, so a round-up never drifts out of date with the catalogue.

export type BestGem = {
  slug: string;
  name: string;
  description: string | null;
  why_gem: string | null;
  category: string;
  subcategory: string;
  license: string | null;
  author: string | null;
  url: string;
  domain: string;
  og_image_url: string | null;
  og_card_url: string | null;
};

export type BestList = {
  slug: string;
  /** Page H1. */
  title: string;
  /** <title> tag; keep under 60 characters where possible. */
  metaTitle: string;
  metaDescription: string;
  /** 100–200 words of hand-written positioning. */
  intro: string;
  /** What earned a place on this list. */
  criteria: string[];
  faq: { q: string; a: string }[];
  /** Subcategories in the library that feed this list. */
  subcategories: string[];
  /** Optional case-insensitive keyword filter over name + description. */
  keywords?: string[];
  limit?: number;
};

export const BEST_LISTS: BestList[] = [
  {
    slug: "gradient-generators",
    title: "Best free gradient generators",
    metaTitle: "Best Free Gradient Generators (Hand-Picked)",
    metaDescription:
      "Hand-picked gradient generators for CSS, SVG and mesh gradients — free, browser-based, and copy-paste ready.",
    intro:
      "Gradients are the cheapest way to make a flat interface feel considered, and the worst possible thing to hand-write. A good generator lets you drag stops, see the interpolation, switch colour space and copy production-ready CSS or SVG in one move. The tools here cover the whole range: simple two-stop linear gradients, mesh and blob gradients for hero backgrounds, noise-and-grain overlays that stop banding on large fills, and the modern OKLCH tooling that keeps mid-tones from going muddy. Everything on the list runs in the browser, needs no account for the basic output, and gives you code rather than a flattened image.",
    criteria: [
      "Outputs code you can paste — CSS, SVG or a token — not just a PNG",
      "Free to use for the core feature without a login",
      "Runs client-side in the browser, so it still works in five years",
      "Handles the hard part: colour interpolation, banding or noise",
    ],
    faq: [
      {
        q: "What is the best free CSS gradient generator?",
        a: "For plain linear and radial CSS gradients, any of the browser-based generators below will do the job in seconds. Pick one that shows the interpolation curve if you care about banding across large areas.",
      },
      {
        q: "How do I stop a gradient from looking banded?",
        a: "Add a small amount of noise or grain on top, or interpolate in OKLCH rather than sRGB. Several tools on this list do one or both for you.",
      },
    ],
    subcategories: ["Gradient backgrounds", "CSS generators", "Background generators"],
    keywords: ["gradient", "mesh", "blob", "noise", "grain"],
  },
  {
    slug: "svg-background-generators",
    title: "Best SVG background generators",
    metaTitle: "Best SVG Background Generators — Free Tools",
    metaDescription:
      "Free SVG generators for waves, blobs, patterns, dividers and section shapes — tuned in the browser, exported as clean SVG.",
    intro:
      "SVG is the right format for decorative page furniture: waves, section dividers, blobs, dot grids, scatter patterns and hero shapes all stay razor sharp at any size and weigh a fraction of an image. The generators collected here let you tune the shape in the browser and export markup you can drop straight into a component or a CSS background. They are especially useful when you are building fast with an AI coding agent — instead of asking a model to invent path data, you hand it a real SVG that already looks right.",
    criteria: [
      "Exports clean SVG markup, not a rasterised download",
      "Real controls: amplitude, seed, complexity, colour",
      "Free, browser-based and account-free for standard output",
      "Output is small enough to inline in a component",
    ],
    faq: [
      {
        q: "Should I inline SVG backgrounds or link them?",
        a: "Inline anything small and decorative that appears once — it removes a request. Link large or repeated shapes so the browser can cache them.",
      },
      {
        q: "Are these SVG generators free for commercial work?",
        a: "The generated shapes are generally yours to use, but licences differ by tool. Each gem page lists the licence where the source states it.",
      },
    ],
    subcategories: ["SVG generators", "SVG generators (fffuel)"],
  },
  {
    slug: "color-palette-tools",
    title: "Best colour palette tools and references",
    metaTitle: "Best Colour Palette Tools for UI Design",
    metaDescription:
      "Palette generators, colour-scale builders and the reference writing that explains how to pick UI colour that actually works.",
    intro:
      "Most colour problems are not solved by another palette generator — they are solved by understanding shade ramps, contrast and perceptual lightness. So this list mixes both: the generators and scale builders you will use on a Tuesday afternoon, and the small number of articles that genuinely change how you choose colour. Between them you can build an accessible ramp from a single brand hue, sanity-check contrast against WCAG, find colour-blind-safe qualitative palettes for charts, and stop reaching for lighten() and darken() functions that quietly destroy saturation.",
    criteria: [
      "Produces a usable ramp or system, not one pretty five-swatch card",
      "Takes accessibility and contrast seriously",
      "Free to use, with values you can export as tokens",
      "Reference material is written by practitioners who ship",
    ],
    faq: [
      {
        q: "How many shades should a UI colour ramp have?",
        a: "Nine to eleven steps per hue covers almost every interface need: backgrounds, borders, text and states. Define them once as tokens rather than deriving them per component.",
      },
      {
        q: "What is the best colour space for UI palettes?",
        a: "OKLCH, where your tooling supports it. It keeps perceived lightness consistent across hues, which is exactly what a ramp needs.",
      },
    ],
    subcategories: ["Color", "Colour scales", "Color theory", "Theming & color"],
  },
  {
    slug: "typography-tools",
    title: "Best typography tools for the web",
    metaTitle: "Best Web Typography Tools & Font Resources",
    metaDescription:
      "Type scales, font pairing tools, variable-font playgrounds and free typeface sources for web and product design.",
    intro:
      "Typography carries more of a design's quality than any other single decision, and the web makes it awkward: variable fonts, fluid scales, subsetting, licensing and fallback metrics all sit between you and a page that reads well. This shelf gathers the tools that remove that friction — scale calculators that give you a fluid clamp() ramp, pairing tools that show real pairings rather than random combinations, variable-font playgrounds for exploring axes before you commit, and the free typeface libraries worth trusting for commercial work.",
    criteria: [
      "Solves a real production problem: scale, pairing, loading or licensing",
      "Shows the type at realistic sizes rather than as specimens only",
      "Free, or free for the part that matters",
      "Licensing is stated clearly where fonts are involved",
    ],
    faq: [
      {
        q: "How do I build a fluid type scale?",
        a: "Pick a base size and ratio for mobile and desktop, then interpolate with clamp() so sizes scale with the viewport. Several calculators here output the CSS directly.",
      },
      {
        q: "Are free fonts safe for commercial projects?",
        a: "Open licences such as OFL are, but always read the licence with the file. Each gem page lists the licence where the source states it.",
      },
    ],
    subcategories: ["Typography"],
  },
  {
    slug: "mockup-and-screenshot-tools",
    title: "Best mockup and screenshot tools",
    metaTitle: "Best Mockup & Screenshot Tools for Product Shots",
    metaDescription:
      "Tools for turning raw screenshots into presentable product shots: device frames, browser mockups, backgrounds and social cards.",
    intro:
      "A raw screenshot never sells the work. Device frames, a considered background, a subtle shadow and the right crop turn the same pixels into something that reads as a product. The tools here do that quickly: browser and phone mockups, annotated screenshots for documentation, social and Open Graph card generators, and the app-store frame templates you need at launch. Most of them are browser-based, which matters when you need a hero image ten minutes before a deadline.",
    criteria: [
      "Fast: from screenshot to shareable image in under a minute",
      "Exports at real resolution, not a watermarked preview",
      "Device frames stay current with actual hardware",
      "Free tier is genuinely usable",
    ],
    faq: [
      {
        q: "What size should an Open Graph image be?",
        a: "1200×630 is the safe default. Keep essential text inside the middle 80% so nothing is lost to platform cropping.",
      },
      {
        q: "Do I need a Mac for good product shots?",
        a: "No. Every tool on this list runs in the browser or cross-platform.",
      },
    ],
    subcategories: ["Mockups & screenshots"],
  },
  {
    slug: "free-icon-sets",
    title: "Best free icon sets",
    metaTitle: "Best Free Icon Sets for Web & App UI",
    metaDescription:
      "Open-source and free icon libraries with consistent grids, multiple weights and licences you can actually ship.",
    intro:
      "Icons are a system, not a shopping trip. Mixing sets is the fastest way to make an interface look assembled rather than designed, so the decision that matters is which single family you adopt — and whether it has the coverage, weights and optical consistency to survive a year of feature work. This list collects the free and open icon libraries worth adopting wholesale, including the ones with React, Vue and Svelte packages, variable weights, and permissive licences that keep legal out of your release.",
    criteria: [
      "Broad enough coverage to avoid mixing families later",
      "Consistent grid, stroke and optical sizing",
      "Permissive licence stated by the project",
      "Ships as components or SVGs you can tree-shake",
    ],
    faq: [
      {
        q: "Can I mix two icon sets?",
        a: "Only if the grid and stroke weight match closely. Otherwise pick one family and commission or draw the handful of icons it is missing.",
      },
      {
        q: "SVG sprite or per-icon components?",
        a: "Per-icon components with tree-shaking for app UIs; a sprite when the same icons repeat many times per page.",
      },
    ],
    subcategories: ["Icons"],
  },
  {
    slug: "shadcn-ui-extensions",
    title: "Best shadcn/ui extensions and blocks",
    metaTitle: "Best shadcn/ui Extensions, Blocks & Registries",
    metaDescription:
      "Component packs, blocks and registries that extend shadcn/ui with everything the base library deliberately leaves out.",
    intro:
      "shadcn/ui gives you primitives you own, which is exactly why the ecosystem around it matters: the base library deliberately stops short of data tables, date pickers, file uploads, dashboards, marketing blocks and animated components. The projects here fill those gaps while keeping the same copy-into-your-repo model, so you can read the code before it lands. If you build with an AI coding agent, this shelf is doubly useful — pointing the agent at a real registry produces far better output than asking it to invent a component from scratch.",
    criteria: [
      "Follows the shadcn registry or copy-in model rather than a black-box package",
      "Accessible primitives underneath, usually Radix",
      "Actively maintained against current shadcn conventions",
      "Free and open source",
    ],
    faq: [
      {
        q: "Are shadcn extensions safe to use in production?",
        a: "You are copying the source into your own repo, so you can audit it. That is the model's main advantage over a dependency.",
      },
      {
        q: "Do these work outside React?",
        a: "Some have Vue, Svelte or Solid ports. The library also has a separate shelf for non-React ports.",
      },
    ],
    subcategories: ["Shadcn extensions", "Registry infrastructure"],
  },
  {
    slug: "react-form-libraries",
    title: "Best form and input libraries",
    metaTitle: "Best React Form & Input Libraries",
    metaDescription:
      "Form builders, validation, input masks, pickers and complex field components that survive real product requirements.",
    intro:
      "Forms are where prototypes go to die. Validation, error messaging, keyboard behaviour, masked inputs, multi-step state, file upload and accessibility all arrive at once, and hand-rolling them is a week you will not get back. This list covers the layers you actually need: schema-driven form state, well-behaved input primitives, specialised fields like phone, currency, date and OTP, and the drag-and-drop builders for teams who want non-developers editing forms.",
    criteria: [
      "Keyboard and screen-reader behaviour handled properly",
      "Works with schema validation rather than ad-hoc checks",
      "Composable — you can restyle it without forking it",
      "Maintained and typed",
    ],
    faq: [
      {
        q: "Do I need a form library for a three-field form?",
        a: "No. Reach for one when validation, async errors or multi-step state appear — that is where hand-rolled forms start leaking bugs.",
      },
      {
        q: "How do I keep forms accessible?",
        a: "Label every control, associate errors with aria-describedby, and move focus to the first invalid field on submit. Good libraries do this for you.",
      },
    ],
    subcategories: ["Forms & inputs"],
  },
  {
    slug: "web-animation-libraries",
    title: "Best web animation libraries",
    metaTitle: "Best Web Animation Libraries for React & JS",
    metaDescription:
      "Animation engines and motion libraries for the web — timelines, springs, layout transitions and gesture-driven motion.",
    intro:
      "Motion is the difference between an interface that works and one that feels alive, but the wrong engine makes everything harder. Timeline-based engines are unbeatable for choreographed sequences; spring-based libraries feel better for anything a user drags or dismisses; and layout-animation libraries solve the specific, painful problem of animating between two DOM states. This list separates them by what they are actually good at, so you can pick by problem rather than by GitHub stars.",
    criteria: [
      "Respects prefers-reduced-motion or makes it easy to",
      "Runs animations off the main thread where possible",
      "Reasonable bundle size for what it does",
      "Documented well enough to use without reverse-engineering demos",
    ],
    faq: [
      {
        q: "Timeline or spring animation?",
        a: "Timelines for choreographed, deterministic sequences. Springs for anything interactive, where the animation must be interruptible.",
      },
      {
        q: "Can I just use CSS?",
        a: "Often, yes — CSS transitions and scroll-driven animations cover a lot now. Reach for a library when you need orchestration, interruption or layout transitions.",
      },
    ],
    subcategories: ["Animation engines", "Easing tools", "CSS animation collections"],
  },
  {
    slug: "scroll-animation-libraries",
    title: "Best scroll animation and scrollytelling tools",
    metaTitle: "Best Scroll Animation & Scrollytelling Libraries",
    metaDescription:
      "Scroll-driven animation libraries, pinning and parallax tools, and scrollytelling frameworks for narrative pages.",
    intro:
      "Scroll is the only interaction every visitor performs, which is why scroll-driven animation carries so much weight on a landing page — and why it goes wrong so often. The tools here cover both ends: low-level scroll-linked animation and pinning for interface work, and full scrollytelling frameworks for narrative and data-led pieces where content advances as the reader moves. Also included are the modern CSS scroll-driven animation references, which now replace a surprising amount of JavaScript.",
    criteria: [
      "Smooth without hijacking native scrolling",
      "Degrades sensibly under prefers-reduced-motion",
      "Does not fight the browser's own scroll-driven animation features",
      "Works on touch as well as it does on a trackpad",
    ],
    faq: [
      {
        q: "Is smooth-scroll hijacking bad for accessibility?",
        a: "It can be. Keep native scroll speed, avoid trapping the user, and provide a reduced-motion path that simply shows the content.",
      },
      {
        q: "Can CSS handle scroll animation now?",
        a: "For many effects, yes — scroll-timeline and view-timeline cover reveals and progress indicators without JavaScript.",
      },
    ],
    subcategories: ["Scroll animation", "Scrollytelling", "Scroll effects"],
  },
  {
    slug: "react-native-ui-kits",
    title: "Best React Native UI kits and animation libraries",
    metaTitle: "Best React Native UI Kits & Animation Libraries",
    metaDescription:
      "Component kits, animation libraries and haptics for React Native apps that need to feel native, not web-in-a-shell.",
    intro:
      "React Native apps live or die on feel. A component kit that looks right in a screenshot can still feel wrong in the hand if gestures lag, transitions run on the JS thread or haptics are missing. This shelf collects the kits and animation libraries that hold up on a real device: worklet-driven animation, gesture handling, native-feeling sheets and navigation, and the haptics packages that make taps land. Together they cover most of what separates a shipped app from a prototype.",
    criteria: [
      "Animation runs on the UI thread, not through the bridge",
      "Gestures and sheets feel native on both platforms",
      "Maintained against current Expo and RN releases",
      "Themeable rather than locked to one visual style",
    ],
    faq: [
      {
        q: "Do these work with Expo?",
        a: "Most do, though some need a development build rather than Expo Go. The project docs state which.",
      },
      {
        q: "Why does my RN animation stutter?",
        a: "Usually because it runs in JavaScript rather than as a worklet on the UI thread. Moving to a worklet-based library fixes most cases.",
      },
    ],
    subcategories: ["RN component kits", "RN animation", "Haptics"],
  },
  {
    slug: "email-component-libraries",
    title: "Best email and notification component libraries",
    metaTitle: "Best Email & Notification Component Libraries",
    metaDescription:
      "Component libraries for transactional email, plus in-app toasts, notification centres and inbox UI.",
    intro:
      "Email is the last place where table layouts and 2003-era HTML still win, so the sane move is to write components and let a renderer produce the markup. This shelf covers that — React-based email component libraries, template collections and preview tooling — alongside the in-app side of the same job: toasts, notification centres and inbox UI. If you are building a product that sends anything, both halves matter, and both are easy to under-invest in until the first customer complaint.",
    criteria: [
      "Renders email HTML that survives Outlook, not just Gmail",
      "Local preview or dev server for iterating on templates",
      "Accessible notification patterns in the in-app components",
      "Free and open source",
    ],
    faq: [
      {
        q: "Why can't I just write HTML emails by hand?",
        a: "You can, but client quirks mean you will rewrite the same table scaffolding constantly. Component libraries encapsulate that.",
      },
      {
        q: "How do I preview transactional email locally?",
        a: "Most libraries here ship a preview server that renders templates in the browser with test data.",
      },
    ],
    subcategories: ["Email & notification"],
  },
  {
    slug: "shader-resources",
    title: "Best shader resources and learning material",
    metaTitle: "Best Shader Resources & Learning Material",
    metaDescription:
      "Shader collections, GLSL learning resources and references for building GPU-side visuals on the web.",
    intro:
      "Shaders are the highest-leverage visual skill on the web: a few dozen lines running on the GPU produce effects no amount of CSS can imitate. They are also the steepest learning curve in front-end, because the mental model is different — you write code for one pixel, not one element. The material here is the fast path: structured learning resources that build intuition, plus collections of readable shaders you can pull apart and modify rather than paste blindly.",
    criteria: [
      "Explains the why, not just the copy-paste",
      "Code is readable and modifiable",
      "Runs in the browser so you can iterate immediately",
      "Free to learn from",
    ],
    faq: [
      {
        q: "Do I need to learn WebGL before shaders?",
        a: "No. Start with fragment shaders in a playground, then learn the WebGL or WebGPU plumbing when you need to ship one.",
      },
      {
        q: "Are shaders bad for performance?",
        a: "They can be excellent for performance — the GPU does the work. The risk is fill rate on large surfaces and battery drain on mobile.",
      },
    ],
    subcategories: ["Shader learning", "Shader collections"],
  },
  {
    slug: "threejs-and-webgl-resources",
    title: "Best Three.js and WebGL resources",
    metaTitle: "Best Three.js & WebGL Resources for the Web",
    metaDescription:
      "Three.js helpers, WebGL learning resources and 3D tooling for building interactive scenes on the web.",
    intro:
      "3D on the web has crossed from novelty into a normal design tool, and Three.js is where most projects start. The hard part is no longer rendering a cube — it is performance, asset pipelines, lighting that does not look like a 2011 tech demo, and knowing which helper library saves a week. This shelf collects the helpers, ecosystem packages and learning resources worth your time, including the ones aimed specifically at designers who want scenes that look art-directed rather than default-lit.",
    criteria: [
      "Saves real work rather than wrapping three lines of Three.js",
      "Performance-aware: instancing, LOD, texture handling",
      "Learning resources build a mental model, not just recipes",
      "Actively maintained against recent Three.js releases",
    ],
    faq: [
      {
        q: "Three.js or raw WebGPU?",
        a: "Three.js for almost everything product-shaped. Go lower-level only when you need control it cannot express.",
      },
      {
        q: "How do I keep a 3D hero from destroying mobile battery?",
        a: "Cap the pixel ratio, pause the render loop off-screen, and give reduced-motion users a static frame.",
      },
    ],
    subcategories: ["Three.js helpers", "3D & WebGL learning", "3D & textures"],
  },
  {
    slug: "css-background-effects",
    title: "Best CSS background and glass effects",
    metaTitle: "Best CSS Background & Glass Effects",
    metaDescription:
      "Liquid glass, particle backgrounds, patterns and modern CSS demos you can copy into a page in minutes.",
    intro:
      "The cheapest visual upgrade available to any page is a background that is not flat white. Liquid glass and frosted panels, subtle particle fields, dot and grid patterns, animated meshes and the wilder modern-CSS demos all cost very little and change the feel of a page immediately. Everything here is close to copy-paste, and most of it is pure CSS — which means no dependency, no hydration cost and no third-party script to audit later.",
    criteria: [
      "Mostly CSS, with JavaScript only where the effect demands it",
      "Cheap enough to run on a mobile GPU",
      "Readable source you can adapt to your tokens",
      "Degrades to something sensible without backdrop-filter",
    ],
    faq: [
      {
        q: "Is backdrop-filter safe to use now?",
        a: "Broadly yes, but always define a solid fallback background so unsupported or low-power contexts stay legible.",
      },
      {
        q: "Do animated backgrounds hurt performance?",
        a: "They can. Animate transform and opacity only, keep the painted area modest, and pause animation when the tab is hidden.",
      },
    ],
    subcategories: [
      "Liquid glass",
      "Glassmorphism",
      "Particle systems",
      "Modern CSS demos",
      "Background patterns",
      "Gradient backgrounds",
    ],
  },
  {
    slug: "text-animation-effects",
    title: "Best text animation and type effect tools",
    metaTitle: "Best Text Animation & Type Effect Libraries",
    metaDescription:
      "Text animation libraries, kinetic typography and CSS type effects for headlines that carry a page.",
    intro:
      "A headline is usually the largest element on a page, which makes it the most efficient place to put motion. Split-text reveals, variable-font weight animation, kinetic typography, gradient and outline treatments, scramble and typewriter effects — all of them earn their keep on a hero, and all of them look amateurish if the timing is wrong. This shelf collects libraries and effect collections that get the timing right by default, plus references for what tasteful type motion actually looks like.",
    criteria: [
      "Animates without breaking text selection or screen readers",
      "Handles line-splitting and reflow properly",
      "Easing defaults that already look right",
      "Honours prefers-reduced-motion",
    ],
    faq: [
      {
        q: "Does split-text animation break accessibility?",
        a: "It can, if each character becomes its own element without an accessible label. Good libraries keep the original text available to assistive tech.",
      },
      {
        q: "How long should a headline animation run?",
        a: "Under 600ms for the whole reveal. Anything slower delays the message the page exists to deliver.",
      },
    ],
    subcategories: ["Text effects", "Text animation"],
  },
  {
    slug: "creative-coding-frameworks",
    title: "Best creative coding frameworks",
    metaTitle: "Best Creative Coding Frameworks & Toolkits",
    metaDescription:
      "Frameworks and toolkits for generative art, canvas sketching and procedural visuals on the web.",
    intro:
      "Creative coding tools optimise for a different loop than product frameworks: fast iteration, deterministic randomness, easy export at print resolution, and getting out of the way while you tune parameters. This shelf covers sketch environments, generative-art toolkits, canvas and 2D rendering libraries, and the utility packages — seeded randomness, geometry, colour maths — that make a generative system reproducible instead of a one-off screenshot.",
    criteria: [
      "Fast feedback loop from edit to visual",
      "Seeded randomness so outputs are reproducible",
      "Export at real resolution for print or video",
      "Small enough to read the source",
    ],
    faq: [
      {
        q: "Canvas or SVG for generative work?",
        a: "Canvas for thousands of elements and pixel effects; SVG when you need crisp scaling or want to edit the output in a vector tool afterwards.",
      },
      {
        q: "How do I make generative art reproducible?",
        a: "Use a seeded random function and store the seed with the piece. Every serious toolkit here supports it.",
      },
    ],
    subcategories: ["Creative coding", "Procedural generation"],
  },
  {
    slug: "charting-libraries",
    title: "Best charting libraries for the web",
    metaTitle: "Best Charting Libraries for Web & React",
    metaDescription:
      "Charting engines and D3 plugins for dashboards, analytics and data-heavy interfaces — picked by what they render well.",
    intro:
      "Charting libraries are not interchangeable. Some are declarative and perfect for standard dashboards; some are low-level and let you draw anything at the cost of writing more code; some are built for hundreds of thousands of points and will beat every competitor on a scatter plot that others cannot render at all. This list is organised around that reality, covering full charting engines and the D3 plugin ecosystem, so you can choose by the shape of your data and the interactivity you need rather than by download count.",
    criteria: [
      "Handles the data volume it claims to, in a real browser",
      "Accessible output: labels, keyboard access, non-colour encoding",
      "Reasonable bundle size or modular imports",
      "Documented API rather than example-driven guesswork",
    ],
    faq: [
      {
        q: "D3 or a charting library?",
        a: "A charting library for standard charts on a deadline; D3 when the visualisation is bespoke and the layout is the interesting part.",
      },
      {
        q: "How do I make charts accessible?",
        a: "Provide a data table alternative, do not encode meaning in colour alone, and label axes and series in the DOM.",
      },
    ],
    subcategories: ["Charting engines", "D3 plugins"],
  },
  {
    slug: "diagram-as-code-tools",
    title: "Best diagram-as-code tools",
    metaTitle: "Best Diagram-as-Code Tools for Docs & Architecture",
    metaDescription:
      "Text-based diagramming tools for architecture, flows and sequence diagrams that live in version control.",
    intro:
      "Diagrams drawn in a canvas app rot the moment the system changes, because nobody wants to reopen the file. Diagram-as-code fixes the incentive: the diagram is text, it lives next to the code, it diffs in a pull request, and an AI agent can update it as part of the same change. This shelf collects the mature text-to-diagram tools — flowcharts, sequence diagrams, architecture views, ERDs — along with the renderers you need to embed them in docs or a site.",
    criteria: [
      "Plain-text source that diffs cleanly in git",
      "Renders in docs, README or the browser without a service",
      "Layout engine good enough to avoid manual nudging",
      "Free and open source",
    ],
    faq: [
      {
        q: "Can AI agents maintain these diagrams?",
        a: "Yes — that is one of the strongest arguments for text-based diagrams. An agent can update the source alongside the code change.",
      },
      {
        q: "Do these work in GitHub READMEs?",
        a: "Several render natively on GitHub; others export SVG you can commit.",
      },
    ],
    subcategories: ["Diagrams as code", "Mapping"],
  },
  {
    slug: "local-first-sync-engines",
    title: "Best local-first and sync engines",
    metaTitle: "Best Local-First & Sync Engines for Web Apps",
    metaDescription:
      "Sync engines, CRDT libraries and local-first databases for apps that stay fast offline and collaborate in real time.",
    intro:
      "Local-first is the architecture behind every app that feels instant: writes land in a local store immediately, sync happens in the background, and conflicts resolve without a spinner. It is also the hardest thing on this shelf to retrofit, so it is worth understanding the options before you commit. The projects here span CRDT libraries, sync engines that pair with Postgres, embedded client databases and full local-first frameworks — each making a different trade between control and how much of the problem it solves for you.",
    criteria: [
      "Real conflict resolution, not last-write-wins by accident",
      "Offline behaviour that survives a long disconnection",
      "Clear story for auth and per-user data access",
      "Production usage or serious documentation behind it",
    ],
    faq: [
      {
        q: "Do I need CRDTs for collaboration?",
        a: "For free-form text and shared canvases, usually yes. For record-level data, a server-authoritative sync engine is often simpler and sufficient.",
      },
      {
        q: "Can I add local-first later?",
        a: "Rarely without a rewrite of the data layer. Decide early if instant, offline-capable interaction is a product requirement.",
      },
    ],
    subcategories: ["Local-first & sync", "Database tooling"],
  },
  {
    slug: "mcp-servers",
    title: "Best MCP servers for design and development",
    metaTitle: "Best MCP Servers for Design & Development",
    metaDescription:
      "Model Context Protocol servers that connect coding agents to design tools, browsers, docs and real systems.",
    intro:
      "MCP is how an agent stops guessing and starts reading your actual systems: your design file, a live browser, your documentation, your database. The ecosystem is young and noisy, so this shelf is deliberately narrow — servers that do one job properly and are usable today, rather than repositories that demo well and break on the second call. Design and UI-generation servers, browser automation, knowledge and documentation access, plus the directories worth watching as the protocol matures.",
    criteria: [
      "Works with mainstream MCP clients without patching",
      "Scoped tools with clear inputs, not one god-tool",
      "Honest about auth and what data leaves your machine",
      "Maintained against current protocol revisions",
    ],
    faq: [
      {
        q: "What is MCP, briefly?",
        a: "Model Context Protocol — a standard way for an AI client to discover and call tools and read resources from an external server.",
      },
      {
        q: "Is it safe to connect an agent to my systems?",
        a: "Only with least-privilege credentials and human approval on writes. Treat every server as code running with your access.",
      },
    ],
    subcategories: [
      "Design MCP",
      "Browser MCP",
      "Knowledge MCP",
      "Dev MCP",
      "UI generation MCP",
      "MCP directories",
      "3D/CAD MCP",
    ],
  },
  {
    slug: "ai-agent-skills",
    title: "Best AI agent skills and skill collections",
    metaTitle: "Best AI Agent Skills & Claude Code Skill Packs",
    metaDescription:
      "Agent skill collections, design-judgment skills and workflow packs that measurably improve coding-agent output.",
    intro:
      "Skills are the highest-return investment in agent work: a well-written skill turns generic model output into something that follows your conventions, and it costs nothing at inference time beyond the tokens it loads. This shelf tracks the collections worth installing — engineering workflow packs, design-judgment skills that stop an agent producing template-shaped UI, security and testing skills, and the marketplaces where new ones appear. Read them before you install them; a skill is a prompt with your name on it.",
    criteria: [
      "Written from real failures rather than speculation",
      "Scoped: one skill, one job, clear trigger conditions",
      "Readable source you can audit and adapt",
      "Maintained as agent tooling changes",
    ],
    faq: [
      {
        q: "Do skills work outside Claude Code?",
        a: "Many are portable — they are structured markdown instructions. Some assume a specific runner's file layout or hooks.",
      },
      {
        q: "How many skills should I install?",
        a: "Fewer than you want to. Every loaded skill competes for attention; install the ones matching work you actually do weekly.",
      },
    ],
    subcategories: [
      "Skill collections",
      "Individual skills",
      "Design skills",
      "Claude Code plugins",
      "Agent rules",
    ],
  },
  {
    slug: "figma-plugins-and-tooling",
    title: "Best Figma plugins and tooling",
    metaTitle: "Best Figma Plugins, API Tools & Code Bridges",
    metaDescription:
      "Figma plugins, API tooling, design-to-code bridges and the alternatives worth knowing if you are leaving Figma.",
    intro:
      "Figma's value now sits as much in its ecosystem as in the canvas: plugins that generate content, token pipelines that keep design and code in sync, API tooling for automating file work, and the design-to-code bridges that let an agent read a frame instead of a screenshot. This shelf gathers all of it, plus a short list of credible alternatives for teams reconsidering their tooling. Pay attention to the token infrastructure entries — that is where most teams get the largest, most durable win.",
    criteria: [
      "Solves a workflow problem rather than adding a novelty",
      "Sane handling of tokens, variables and naming",
      "Does not require handing over the whole file to a third party",
      "Maintained against current Figma plugin APIs",
    ],
    faq: [
      {
        q: "What is the best way to get Figma designs into code?",
        a: "Tokens and a component contract beat pixel-perfect export every time. Use a bridge to read structure and variables, not to generate markup wholesale.",
      },
      {
        q: "Are there good Figma alternatives?",
        a: "Yes, several — including open-source and code-native options. They are listed on this shelf.",
      },
    ],
    subcategories: [
      "Figma API & plugin dev",
      "Figma ecosystem",
      "Figma to code",
      "Figma alternatives",
      "Token infrastructure",
    ],
  },
  {
    slug: "public-design-systems",
    title: "Best public design systems to learn from",
    metaTitle: "Best Public Design Systems to Learn From",
    metaDescription:
      "Open, well-documented design systems worth reading end to end — components, tokens, accessibility and governance.",
    intro:
      "A public design system is free documentation of decisions you would otherwise make twice, argued about, and get wrong the first time. The best ones publish not just components but the reasoning: token structure, contrast rules, motion principles, content style and how the system is governed. This shelf collects the systems worth reading end to end — government systems that take accessibility seriously, product systems with mature component APIs, and the token infrastructure that keeps them synchronised with code.",
    criteria: [
      "Published documentation, not just a component library",
      "Accessibility rules stated explicitly and tested",
      "Token architecture you can learn from",
      "Actively maintained by the organisation using it",
    ],
    faq: [
      {
        q: "Should a small team build a design system?",
        a: "Build tokens and a handful of primitives. Adopt an existing system for the rest until component churn justifies owning more.",
      },
      {
        q: "Which public system is best for accessibility?",
        a: "Government systems generally set the bar, because they are legally obliged to meet it and publish their testing.",
      },
    ],
    subcategories: ["Public design systems", "DS process & docs", "Accessibility"],
  },
  {
    slug: "saas-boilerplates",
    title: "Best open-source SaaS boilerplates and starters",
    metaTitle: "Best Open-Source SaaS Boilerplates & Starters",
    metaDescription:
      "SaaS boilerplates and AI app starters with auth, billing and multi-tenancy already wired — free and open source.",
    intro:
      "The boring parts of a SaaS — auth, billing, teams, roles, transactional email, an admin view — take weeks and are identical everywhere. A good boilerplate hands you those weeks back, and an open-source one lets you read exactly what you are inheriting first. This shelf collects the starters worth forking, including AI application templates with streaming and tool-calling already handled. Judge them on how easy they are to strip down: the best boilerplate is one you can delete half of.",
    criteria: [
      "Auth, billing and tenancy actually implemented, not stubbed",
      "Readable structure you can strip back",
      "Open source with a permissive licence",
      "Recent commits — a stale boilerplate is a liability",
    ],
    faq: [
      {
        q: "Is a boilerplate worth it if I use an AI coding agent?",
        a: "Often more so. An agent works far better inside an existing, coherent structure than on an empty folder.",
      },
      {
        q: "What should I check before forking?",
        a: "The licence, the last commit date, the test coverage on billing, and how tightly it couples to one hosting provider.",
      },
    ],
    subcategories: [
      "Open-source SaaS boilerplate",
      "AI app starter",
      "Open-source product to fork",
    ],
  },
  {
    slug: "web-game-engines",
    title: "Best web game engines",
    metaTitle: "Best Web Game Engines & Frameworks",
    metaDescription:
      "Browser game engines and frameworks for 2D and 3D games, from tiny jam-sized libraries to full editors.",
    intro:
      "Browser games have quietly become viable again: WebGL and WebGPU are fast, distribution is a URL, and the tooling has matured. The right engine depends entirely on scope. Jam-sized 2D games want something you can learn in an evening; a commercial project wants an editor, asset pipeline and physics; and a 3D game on the web means committing to a renderer early. This shelf splits the field so you can pick by scope, plus the netcode libraries you will need the moment two people play at once.",
    criteria: [
      "Ships games, not just demos",
      "Asset pipeline and build size suited to the web",
      "Input, audio and physics either included or well-integrated",
      "Documentation and community large enough to unblock you",
    ],
    faq: [
      {
        q: "What is the easiest web game engine to start with?",
        a: "A small 2D framework with a single-file API. You will learn the game loop without fighting an editor.",
      },
      {
        q: "Can browser games handle multiplayer?",
        a: "Yes — WebSocket and WebRTC netcode libraries on this shelf handle rooms, state sync and rollback.",
      },
    ],
    subcategories: ["Web game engines", "Multiplayer & netcode", "PixiJS ecosystem"],
  },
  {
    slug: "public-domain-image-archives",
    title: "Best public domain image and media archives",
    metaTitle: "Best Public Domain Image & Media Archives",
    metaDescription:
      "Public domain and openly licensed image, illustration and video archives with licences you can actually verify.",
    intro:
      "Stock photography makes everything look the same. Public domain archives do the opposite: vintage botanical plates, scientific illustration, museum collections, historical photography and film stills give a project a visual point of view that no generic stock library can. The archives here are large, genuinely open, and — critically — clear about licensing, so you can verify status before something ships. Several offer high-resolution downloads suitable for print as well as screen.",
    criteria: [
      "Licence status stated clearly per item or collection",
      "High-resolution downloads, not thumbnails",
      "Institutional or long-lived, so links do not rot",
      "Searchable well enough to actually find something",
    ],
    faq: [
      {
        q: "Is public domain the same as free to use?",
        a: "Usually, but some institutions add their own terms to digital reproductions. Check the collection's stated policy before commercial use.",
      },
      {
        q: "Can I use these images in a client project?",
        a: "Public domain material generally yes. Keep a record of the source and licence for each asset you ship.",
      },
    ],
    subcategories: ["Public domain archives", "Image & video assets", "Branding & graphic archives"],
  },
  {
    slug: "css-loaders-and-spinners",
    title: "Best CSS loaders and spinners",
    metaTitle: "Best CSS Loaders, Spinners & Skeletons",
    metaDescription:
      "Copy-paste CSS loaders, spinners and skeleton patterns that communicate progress without adding a dependency.",
    intro:
      "Loading states are the most-seen and least-designed part of most products. A pure-CSS loader costs nothing, cannot fail to hydrate, and — done well — makes a two-second wait feel shorter than a one-second wait with no feedback at all. This shelf collects copy-paste loader collections and the skeleton patterns worth preferring for content-shaped waits, where a shimmering outline of the real layout beats a spinning circle every time.",
    criteria: [
      "Pure CSS where possible — no dependency, no hydration cost",
      "Announces state to assistive tech, or is easy to make do so",
      "Cheap to animate: transform and opacity only",
      "Adaptable to your own tokens and sizes",
    ],
    faq: [
      {
        q: "Spinner or skeleton?",
        a: "Skeleton when you know the shape of the incoming content; spinner for short, indeterminate waits inside a button or small region.",
      },
      {
        q: "How do I make loaders accessible?",
        a: "Use aria-busy on the region and an aria-live status message, and never rely on the animation alone to convey state.",
      },
    ],
    subcategories: ["Loaders & spinners"],
  },
  {
    slug: "free-audio-libraries",
    title: "Best free audio and sound effect libraries",
    metaTitle: "Best Free Audio & Sound Effect Libraries",
    metaDescription:
      "Free music, sound effects and game audio libraries with clear licensing for products, games and video.",
    intro:
      "Sound is the most under-used channel in digital products, partly because licensing is a minefield. The libraries here are the ones with clear terms: free music beds for video, UI sound sets for interfaces, and game audio collections covering effects, ambience and adaptive music. Also included are the procedural audio tools that generate effects on the fly, which are often a better answer than shipping a folder of WAVs — especially for games and interactive toys.",
    criteria: [
      "Licence terms stated plainly, including commercial use",
      "Quality high enough to ship without remastering",
      "Sensible file formats and sizes for the web",
      "Free tier that does not require attribution gymnastics",
    ],
    faq: [
      {
        q: "Do I need attribution for free audio?",
        a: "It depends on the licence — CC-BY does, CC0 does not. Each gem page lists the licence where the source states it.",
      },
      {
        q: "Should UI sounds be on by default?",
        a: "No. Ship them off, make the toggle obvious, and respect the system's silent state on mobile.",
      },
    ],
    subcategories: ["Free audio", "Game audio"],
  },
  {
    slug: "pixel-art-and-dithering-tools",
    title: "Best pixel art and dithering tools",
    metaTitle: "Best Pixel Art & Dithering Tools",
    metaDescription:
      "Pixel art editors, retro tooling and dithering utilities for lo-fi visuals that still look deliberate.",
    intro:
      "Dithering and pixel art have come back as design languages, not just nostalgia — a dithered image is tiny, distinctive and instantly readable as a deliberate choice. This shelf covers both halves: editors and palettes for making genuine pixel art, and the dithering utilities that convert photographs into ordered or error-diffused patterns for use as textures, backgrounds and print-adjacent visuals. Several run entirely in the browser, so you can test the look on your own images before committing to it.",
    criteria: [
      "Real control over palette and dithering algorithm",
      "Outputs at the resolution you actually need",
      "Browser-based or cross-platform",
      "Free for the core workflow",
    ],
    faq: [
      {
        q: "What dithering algorithm should I use?",
        a: "Ordered (Bayer) for a graphic, patterned look; Floyd–Steinberg error diffusion when you want photographic detail to survive.",
      },
      {
        q: "How do I keep pixel art crisp on the web?",
        a: "Set image-rendering: pixelated and scale by whole-number factors only.",
      },
    ],
    subcategories: ["Pixel art & retro", "Dithering & pixel"],
  },
  {
    slug: "ux-pattern-libraries",
    title: "Best UX pattern libraries and inspiration galleries",
    metaTitle: "Best UX Pattern Libraries & Design Galleries",
    metaDescription:
      "Pattern libraries, UI galleries and micro-interaction collections for looking up how good products solve a problem.",
    intro:
      "When you are stuck on onboarding, an empty state, a pricing page or a settings screen, the fastest unblock is seeing how twenty good products solved it. Pattern libraries and galleries are that lookup table. This shelf separates them by what they actually catalogue — full-page galleries for layout and marketing, pattern libraries for flows and interaction, micro-element collections for the small stuff, and motion galleries for reference before you tune easing. Use them as references, not as a source of things to copy verbatim.",
    criteria: [
      "Curated rather than an infinite scrape",
      "Catalogued by pattern or flow, so it is searchable",
      "Updated recently enough to reflect current practice",
      "Free to browse without an account wall",
    ],
    faq: [
      {
        q: "How do I use inspiration galleries without copying?",
        a: "Collect three or four solutions to the same problem and extract the underlying decision, not the visual treatment.",
      },
      {
        q: "Are pattern libraries a substitute for user research?",
        a: "No. They tell you what is conventional, which lowers risk — not what your specific users need.",
      },
    ],
    subcategories: [
      "UX pattern libraries",
      "Web design galleries",
      "SaaS & landing galleries",
      "Micro-element galleries",
      "Motion & delight galleries",
    ],
  },
  {
    slug: "developer-utilities",
    title: "Best developer utilities for shipping faster",
    metaTitle: "Best Developer Utilities & DX Tooling",
    metaDescription:
      "Type-safe TypeScript helpers, build and DX CLIs, visual QA tools and the small packages that remove a day of work.",
    intro:
      "Most productivity gains are not frameworks — they are small, boring packages that remove one recurring day of work. Type-safe helpers that make invalid states unrepresentable, build and DX CLIs that cut a minute off every run, visual QA tooling that catches regressions before a human does, and performance debugging utilities that turn a vague slowness complaint into a flame graph. This shelf collects those. They are also exactly the kind of dependency an AI coding agent handles well, because they are stable, well typed and well documented.",
    criteria: [
      "Does one thing and is easy to remove later",
      "Properly typed, with types that catch real mistakes",
      "Stable API and real maintenance",
      "Works in a normal CI environment without exotic setup",
    ],
    faq: [
      {
        q: "How do I avoid dependency bloat?",
        a: "Prefer packages you could plausibly rewrite in a day, and audit the tree after every install.",
      },
      {
        q: "Which utilities help most with AI-assisted coding?",
        a: "Strong typing and fast feedback loops. Both narrow the space in which an agent can produce something plausible but wrong.",
      },
    ],
    subcategories: [
      "Type-safe TypeScript",
      "Build & DX CLI",
      "Visual QA & testing",
      "Perf & debugging",
      "UnJS toolkit",
    ],
  },
];

export const BEST_BY_SLUG: Record<string, BestList> = Object.fromEntries(
  BEST_LISTS.map((l) => [l.slug, l]),
);

/**
 * Round-ups a given gem belongs to, using the same subcategory + keyword rule
 * the round-up pages themselves use to select entries. Used to link a gem
 * detail page back to its topic hubs, so the round-ups gain internal links
 * from the pages that already rank.
 */
export function bestListsForGem(
  gem: { subcategory?: string | null; name?: string | null; description?: string | null },
  limit = 3,
): BestList[] {
  const sub = (gem.subcategory ?? "").toLowerCase();
  if (!sub) return [];
  const haystack = `${gem.name ?? ""} ${gem.description ?? ""}`.toLowerCase();
  const matches = BEST_LISTS.filter((list) => {
    if (!list.subcategories.some((s) => s.toLowerCase() === sub)) return false;
    if (!list.keywords || list.keywords.length === 0) return true;
    return list.keywords.some((k) => haystack.includes(k.toLowerCase()));
  });
  return matches.slice(0, limit);
}

/** Groups used on the round-up index page. */
export const BEST_GROUPS: { title: string; blurb: string; slugs: string[] }[] = [
  {
    title: "Design tools and generators",
    blurb: "Generators and utilities that replace an afternoon of fiddling.",
    slugs: [
      "gradient-generators",
      "svg-background-generators",
      "color-palette-tools",
      "typography-tools",
      "mockup-and-screenshot-tools",
      "free-icon-sets",
    ],
  },
  {
    title: "Interface and components",
    blurb: "What to build the actual product out of.",
    slugs: [
      "shadcn-ui-extensions",
      "react-form-libraries",
      "email-component-libraries",
      "react-native-ui-kits",
      "css-loaders-and-spinners",
      "public-design-systems",
    ],
  },
  {
    title: "Motion, effects and 3D",
    blurb: "The layer that makes a page feel alive.",
    slugs: [
      "web-animation-libraries",
      "scroll-animation-libraries",
      "text-animation-effects",
      "css-background-effects",
      "shader-resources",
      "threejs-and-webgl-resources",
      "creative-coding-frameworks",
    ],
  },
  {
    title: "Data, dev and architecture",
    blurb: "Charts, diagrams, sync and the unglamorous tooling that ships work.",
    slugs: [
      "charting-libraries",
      "diagram-as-code-tools",
      "local-first-sync-engines",
      "developer-utilities",
      "saas-boilerplates",
    ],
  },
  {
    title: "AI, agents and workflow",
    blurb: "Tooling for building with coding agents rather than around them.",
    slugs: ["mcp-servers", "ai-agent-skills", "figma-plugins-and-tooling"],
  },
  {
    title: "Games, media and reference",
    blurb: "Assets, engines and the galleries worth returning to.",
    slugs: [
      "web-game-engines",
      "public-domain-image-archives",
      "free-audio-libraries",
      "pixel-art-and-dithering-tools",
      "ux-pattern-libraries",
    ],
  },
];
